import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Building2, User, Truck, BarChart3, Search, Wallet } from "lucide-react";

const TABS = [
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "persons", label: "Persons", icon: User },
  { id: "drivers", label: "Drivers", icon: Truck },
  { id: "staff", label: "Staff Expenses", icon: Wallet },
];

export default function Summary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("companies");
  const [q, setQ] = useState("");
  const [staff, setStaff] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    api.get("/reports/entity-summary").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  useEffect(() => {
    if (tab !== "staff") return;
    setStaff(null);
    api.get("/reports/staff-expenses", { params: { start, end } })
      .then((r) => setStaff(r.data)).catch(() => setStaff({ error: true }));
  }, [tab, start, end]);

  const rows = useMemo(() => {
    if (!data || data.error) return [];
    const list = data[tab] || [];
    const term = q.trim().toLowerCase();
    return term ? list.filter((r) => r.name.toLowerCase().includes(term)) : list;
  }, [data, tab, q]);

  const staffRows = useMemo(() => {
    if (!staff || staff.error) return [];
    const term = q.trim().toLowerCase();
    return term ? (staff.staff || []).filter((r) => r.name.toLowerCase().includes(term)) : (staff.staff || []);
  }, [staff, q]);

  function open(r) {
    if (tab === "companies") {
      if (r.id) navigate(`/entity/company/${r.id}`);
      else navigate(`/entity/customer/${encodeURIComponent(r.name)}`);
    } else if (tab === "persons") {
      navigate(`/entity/contact/${r.id}`);
    } else if (tab === "drivers") {
      if (r.id) navigate(`/entity/driver/${r.id}`);
      else navigate(`/entity/driver/${encodeURIComponent(r.name)}`);
    }
  }

  if (!data) return <div className="text-sm text-[#68665E]">Loading…</div>;
  if (data.error) return <div className="text-sm text-[#8A2526]">Could not load summary.</div>;

  return (
    <div data-testid="summary-page">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-8 h-8 text-[#C84B31]" />
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Summary</div>
          <h1 className="font-headings text-3xl md:text-4xl font-bold text-[#1A1A18]">Work & Billing Summary</h1>
          <p className="text-sm text-[#68665E] mt-1">Click a company/person/driver to drill down, or check per-staff expense totals.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setQ(""); }} data-testid={`summary-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${tab === t.id ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white text-[#68665E] border-[#E5E3DB] hover:border-[#C84B31]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A09E96]" />
          <input className="input-field pl-9 w-64" placeholder={`Search ${tab}...`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="summary-search" />
        </div>
      </div>

      {tab === "staff" ? (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div><label className="input-label">From</label><input type="date" className="input-field" value={start} onChange={(e) => setStart(e.target.value)} data-testid="staff-exp-start" /></div>
            <div><label className="input-label">To</label><input type="date" className="input-field" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="staff-exp-end" /></div>
            {(start || end) && <button className="btn-ghost text-xs" onClick={() => { setStart(""); setEnd(""); }} data-testid="staff-exp-clear">Clear dates</button>}
            {staff && !staff.error && <div className="ml-auto text-sm text-[#1A1A18] font-semibold" data-testid="staff-exp-grand-total">Total: {formatINR(staff.total)}</div>}
          </div>
          <div className="table-wrap">
            <table className="w-full">
              <thead><tr><th className="table-th">Staff</th><th className="table-th text-right">Entries</th><th className="table-th text-right">Total Expenses</th></tr></thead>
              <tbody>
                {!staff ? (
                  <tr><td className="table-td text-center text-[#68665E]" colSpan={3}>Loading…</td></tr>
                ) : staffRows.length === 0 ? (
                  <tr><td className="table-td text-center text-[#68665E]" colSpan={3}>No staff expenses in this range.</td></tr>
                ) : staffRows.map((r) => (
                  <tr key={r.name} className="table-row" data-testid={`staff-exp-row-${r.name}`}>
                    <td className="table-td font-medium">{r.name}</td>
                    <td className="table-td text-right">{r.count}</td>
                    <td className="table-td text-right font-medium text-[#8A2526]">{formatINR(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="table-wrap">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-th">Name</th>
                {tab === "persons" && <th className="table-th">Company / Dept</th>}
                <th className="table-th text-right">LRs</th>
                <th className="table-th text-right">Total Freight</th>
                {tab === "companies" && <th className="table-th text-right">Paid</th>}
                {tab === "companies" && <th className="table-th text-right">Outstanding</th>}
                <th className="table-th text-right">Bill Not Generated</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className="table-td text-center text-[#68665E]" colSpan={7}>No records.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.key} className="table-row cursor-pointer" onClick={() => open(r)} data-testid={`summary-row-${r.key}`}>
                  <td className="table-td font-medium text-[#C84B31]">{r.name}</td>
                  {tab === "persons" && <td className="table-td text-[#68665E] text-xs">{[r.company_name, r.department_name].filter(Boolean).join(" · ") || "—"}</td>}
                  <td className="table-td text-right">{r.lr_count}</td>
                  <td className="table-td text-right font-medium">{formatINR(r.total_freight)}</td>
                  {tab === "companies" && <td className="table-td text-right text-[#1f7a3d]">{formatINR(r.paid)}</td>}
                  {tab === "companies" && <td className="table-td text-right text-[#8A2526]">{formatINR(r.outstanding)}</td>}
                  <td className="table-td text-right">
                    {r.not_generated_count > 0
                      ? <span className="inline-block px-2 py-0.5 text-[11px] font-semibold bg-[#FBEAE7] text-[#8A2526] border border-[#E8B4AC]">{r.not_generated_count}</span>
                      : <span className="text-[#A09E96]">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
